import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import styles from "./Home.module.css";
import Footer from "../../components/ui/Footer";
import Navbar from "../../components/ui/Navbar";
import Menu from "../../components/ui/Menu";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FixedAgent from "../../utils/api/FixedAgent";
import Auth from "../../utils/api/Auth";
import { userDataActions } from "../../store/redux-store/Index";
import {
  getCurrentGroup,
  CAMPAIGN_IMAGE,
  REWARDS_HEADER_IMAGE,
} from "../rewards/rewardData";
import useRewardsProgramme from "../rewards/useRewardsProgramme";

const PIN_TYPE_LABELS = {
  PrivateMotor: "Private Motor",
  Commercial: "Commercial",
  TriCycle: "Tri Cycle",
  MotorCycle: "Motor Cycle",
  Truck: "Truck",
};

const QUICK_ACTIONS = [
  {
    title: "Insurance",
    to: "/renew",
    icon: "shield",
    tone: "blueTone",
  },
  {
    title: "Customers",
    to: "/customers",
    icon: "users",
    tone: "greenTone",
  },
  {
    title: "My Policies",
    to: "/history",
    icon: "document",
    tone: "purpleTone",
  },
  {
    title: "PIN",
    to: "/pin",
    icon: "pin",
    tone: "amberTone",
  },
];

const normalizePins = (responseData) => {
  const rawData = responseData?.data;

  if (Array.isArray(rawData)) {
    return rawData;
  }

  if (Array.isArray(rawData?.data)) {
    return rawData.data;
  }

  return [];
};

const formatPinType = (type) => {
  if (!type) {
    return "Unassigned";
  }

  return PIN_TYPE_LABELS[type] || String(type).replace(/([a-z])([A-Z])/g, "$1 $2");
};

const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (!parts.length) {
    return "AP";
  }

  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

const summarizePins = (pins) => {
  const activePins = pins.filter(
    (pin) => String(pin?.status || "").toUpperCase() === "ACTIVE",
  );

  const platformMap = activePins.reduce((summary, pin) => {
    const platform = pin?.platform || "Unknown";
    const type = formatPinType(pin?.type);

    if (!summary[platform]) {
      summary[platform] = {
        platform,
        count: 0,
        exportable: 0,
        standard: 0,
        types: {},
      };
    }

    summary[platform].count += 1;
    summary[platform].types[type] = (summary[platform].types[type] || 0) + 1;

    if (String(pin?.export_option || "").toUpperCase() === "YES") {
      summary[platform].exportable += 1;
    } else {
      summary[platform].standard += 1;
    }

    return summary;
  }, {});

  const typeMap = activePins.reduce((summary, pin) => {
    const type = formatPinType(pin?.type);
    summary[type] = (summary[type] || 0) + 1;
    return summary;
  }, {});

  const platformBreakdown = Object.values(platformMap).sort(
    (first, second) => second.count - first.count,
  );

  const typeBreakdown = Object.entries(typeMap)
    .map(([type, count]) => ({ type, count }))
    .sort((first, second) => second.count - first.count);

  return {
    activePins,
    totalActive: activePins.length,
    exportable: activePins.filter(
      (pin) => String(pin?.export_option || "").toUpperCase() === "YES",
    ).length,
    standard: activePins.filter(
      (pin) => String(pin?.export_option || "").toUpperCase() !== "YES",
    ).length,
    platformBreakdown,
    typeBreakdown,
    recentActivePins: activePins.slice(0, 3),
  };
};

const getGreeting = () => {
  const currentTime = new Date().getHours();

  if (currentTime < 12) {
    return "Good Morning";
  }

  if (currentTime < 18) {
    return "Good Afternoon";
  }

  return "Good Evening";
};

const getLocalDateKey = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

const getRewardCelebrationKey = (agentKey) =>
  `agentpro:reward-celebration:${agentKey}:${getLocalDateKey()}`;

const ActionIcon = ({ type }) => {
  switch (type) {
    case "users":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm8.5-1a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7ZM2 20c0-4 2.7-7 6-7s6 3 6 7v1H2v-1Zm12.3 1c.1-.4.2-.8.2-1.2 0-2.3-.9-4.4-2.3-5.9 1.1-.8 2.5-1.2 4-1.2 3.2 0 5.8 2.7 5.8 6.3v2h-7.7Z" />
        </svg>
      );
    case "document":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 2h8l5 5v15H6V2Zm7 1.8V8h4.2L13 3.8ZM8 11h8v2H8v-2Zm0 4h8v2H8v-2Zm0-8h3v2H8V7Z" />
        </svg>
      );
    case "pin":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 3h10v5l2 2v3h-6v8l-1 1-1-1v-8H5v-3l2-2V3Zm2 2v4.1L7.1 11h9.8L15 9.1V5H9Z" />
        </svg>
      );
    case "arrow":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 4 10.6 5.4 16.2 11H4v2h12.2l-5.6 5.6L12 20l8-8-8-8Z" />
        </svg>
      );
    case "shieldCheck":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 2 4 5.4V11c0 5 3.4 9.7 8 11 4.6-1.3 8-6 8-11V5.4L12 2Zm-1.1 13.6-3.2-3.2 1.4-1.4 1.8 1.8 4.4-4.4 1.4 1.4-5.8 5.8Z" />
        </svg>
      );
    case "trophy":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8 3h8v3h3v2c0 3-1.9 5-4.6 5.7-.5 1.2-1.4 2.1-2.4 2.5V19h3v2H9v-2h3v-2.8c-1.1-.4-1.9-1.3-2.4-2.5C6.9 13 5 11 5 8V6h3V3Zm-1 5c0 1.5.8 2.6 2.1 3.2A8.4 8.4 0 0 1 9 10V8H7Zm8 2c0 .4 0 .8-.1 1.2C16.2 10.6 17 9.5 17 8h-2v2Z" />
        </svg>
      );
    case "shield":
    default:
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 2 4 5.4V11c0 5 3.4 9.7 8 11 4.6-1.3 8-6 8-11V5.4L12 2Zm1 13h-2v-2H9v-2h2V9h2v2h2v2h-2v2Z" />
        </svg>
      );
  }
};

function Home(props) {
  const [pinDetails, setPinDetails] = useState({
    pins: [],
    isLoading: true,
    error: "",
  });
  const [showRewardCelebration, setShowRewardCelebration] = useState(false);

  const token = useSelector((state) => state.userData.loginData.token);
  const loginData = useSelector((state) => state.userData.loginData);
  const user = useSelector((state) => state.userData.personalInfo);
  const dispatch = useDispatch();

  const displayName =
    user?.fullname ||
    loginData?.fullname ||
    loginData?.name ||
    loginData?.user?.fullname ||
    loginData?.user?.[0]?.fullname ||
    loginData?.data?.fullname ||
    loginData?.data?.user?.fullname ||
    loginData?.data?.user?.[0]?.fullname ||
    loginData?.email?.split("@")?.[0] ||
    "Agent";
  const initials = getInitials(displayName);
  const rewardCelebrationAgentKey =
    user?.id ||
    user?.email ||
    loginData?.id ||
    loginData?.user_id ||
    loginData?.email ||
    loginData?.user?.id ||
    loginData?.user?.email ||
    loginData?.user?.[0]?.id ||
    loginData?.user?.[0]?.email ||
    displayName;
  const pinSummary = useMemo(
    () => summarizePins(pinDetails.pins),
    [pinDetails.pins],
  );
  const { groups, policyCount, isLoading: rewardsLoading, error: rewardsError } = useRewardsProgramme(token);
  const currentRewardGroup = getCurrentGroup(groups, policyCount);
  const closeMenuHandler = () => {
    props.setShowMenu(false);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchPinDetails = async () => {
      if (!token) {
        setPinDetails({ pins: [], isLoading: false, error: "" });
        return;
      }

      try {
        setPinDetails((current) => ({
          ...current,
          isLoading: true,
          error: "",
        }));

        const pinListRes = await FixedAgent.getDashboardPinList({ token, status: "ACTIVE" });

        if (isMounted) {
          setPinDetails({
            pins: normalizePins(pinListRes.data),
            isLoading: false,
            error: "",
          });
        }
      } catch (err) {
        const message =
          err?.response?.data?.message || "Failed to fetch active PIN details";

        if (isMounted) {
          setPinDetails({
            pins: [],
            isLoading: false,
            error: message,
          });
        }

        toast.error(message, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
        });
      }
    };

    fetchPinDetails();

    return () => {
      isMounted = false;
    };
  }, [token]);

  useEffect(() => {
    let isMounted = true;

    const fetchUserDetails = async () => {
      if (!token) return;

      try {
        const userRes = await Auth.getUserInfo({ token });
        if (isMounted && userRes?.data?.user?.[0]) {
          dispatch(userDataActions.generalUserData(userRes.data.user[0]));
        }
      } catch (err) {
        console.error("Failed to fetch user info:", err);
      }
    };

    fetchUserDetails();

    return () => {
      isMounted = false;
    };
  }, [token, dispatch]);

  useEffect(() => {
    if (rewardsLoading || !currentRewardGroup || !rewardCelebrationAgentKey) {
      return;
    }

    const storageKey = getRewardCelebrationKey(rewardCelebrationAgentKey);

    try {
      if (window.localStorage.getItem(storageKey)) return;
      window.localStorage.setItem(storageKey, "shown");
    } catch (error) {
      console.warn("Unable to persist the daily reward celebration state.", error);
    }

    setShowRewardCelebration(true);
  }, [currentRewardGroup, rewardCelebrationAgentKey, rewardsLoading]);

  useEffect(() => {
    if (!showRewardCelebration) return undefined;

    const originalOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setShowRewardCelebration(false);
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [showRewardCelebration]);

  // Legacy dashboard API calls paused by request:
  // - FixedAgent.getManualOrderHistory({ token }) for the old sales/free PIN meter.
  // - FixedAgent.getAllCustomers({ token }) for the old customer status cards.
  // - Auth.getUserInfo({ token }) for dashboard-only profile refresh.
  // - Auth.getAgentBirthday() and direct Airtable fraud/banner fetches.

  return (
    <div>
      <ToastContainer />
      {showRewardCelebration && currentRewardGroup && (
        <div
          className={styles.rewardCelebrationBackdrop}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setShowRewardCelebration(false);
            }
          }}
        >
          <section
            className={styles.rewardCelebrationDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="reward-celebration-title"
          >
            <button
              type="button"
              className={styles.rewardCelebrationClose}
              aria-label="Close reward message"
              onClick={() => setShowRewardCelebration(false)}
              autoFocus
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="m6 6 12 12M18 6 6 18" />
              </svg>
            </button>

            <img
              className={styles.rewardCelebrationGift}
              src={currentRewardGroup.image}
              alt={currentRewardGroup.reward}
            />
            <div className={styles.rewardCelebrationCopy}>
              <h2 id="reward-celebration-title">Congratulations!</h2>
              <p>
                You qualified for {currentRewardGroup.name} and earned{" "}
                <strong>{currentRewardGroup.reward}</strong>.
              </p>
            </div>
          </section>
        </div>
      )}
      <div className="menu" onClick={closeMenuHandler}>
        {props.showMenu && <Menu setShowMenu={props.setShowMenu} />}
      </div>
      <Navbar title="Dashboard" setShowMenu={props.setShowMenu} />
      <main className={styles.agentDashboardBody}>
        <section className={styles.agentHero}>
          <div className={styles.agentGreeting}>
            <p>{getGreeting()},</p>
            <h1>{displayName}</h1>
            <span>Let's grow your insurance business today.</span>
          </div>
          <div className={styles.agentAvatar}>{initials}</div>
        </section>

        <section className={styles.agentSection}>
          <h2 className={styles.sectionTitle}>Quick Actions</h2>
          <div className={styles.quickActionsGrid}>
            {QUICK_ACTIONS.map((action) => (
              <Link
                key={action.title}
                to={action.to}
                className={styles.quickActionCard}
              >
                <span
                  className={`${styles.quickActionIcon} ${
                    styles[action.tone]
                  }`}
                >
                  <ActionIcon type={action.icon} />
                </span>
                <strong>{action.title}</strong>
              </Link>
            ))}
          </div>
        </section>

        <div className={styles.rewardHomeSectionHeader}>
          <h2>Agent Incentives</h2>
          <Link to="/rewards">
            View details <ActionIcon type="arrow" />
          </Link>
        </div>

        <div className={styles.rewardHomeWrap}>
          <Link
            to="/rewards"
            className={styles.rewardHomeCard}
          >
            <div className={styles.rewardHomeContent}>
              <div className={styles.rewardHomeTierRow}>
                <div className={styles.rewardHomeTierBadge}>
                  {currentRewardGroup?.id || "?"}
                </div>
                <div className={styles.rewardHomeTierMeta}>
                  <strong className={styles.rewardHomeTierName}>
                    {currentRewardGroup?.name || "Rewards programme"}
                  </strong>
                </div>
              </div>

              <div className={styles.rewardHomeHero}>
                <span className={styles.rewardHomeHeroNum}>{rewardsLoading ? "…" : policyCount ?? "–"}</span>
                <span className={styles.rewardHomeHeroCopy}>
                  <strong>Points</strong>
                </span>
              </div>

              <div className={styles.rewardHomeFooter}>
                <p className={styles.rewardHomeNextMsg}>
                  {rewardsError
                    ? "Reward progress is temporarily unavailable."
                    : currentRewardGroup
                      ? `You currently qualify for ${currentRewardGroup.name}.`
                      : "Issue 50 policies to enter Group G."}
                </p>
                <span className={styles.rewardHomeCTA}>
                  View details <ActionIcon type="arrow" />
                </span>
              </div>
            </div>

            <div className={styles.rewardHomeCampaignScene}>
              <img
                className={styles.rewardHomeCampaignMark}
                src={CAMPAIGN_IMAGE}
                alt="Transact More, Get More"
              />
              <img
                className={styles.rewardHomeOverallGift}
                src={REWARDS_HEADER_IMAGE}
                alt="Agent rewards gift collection"
              />
              <div className={styles.rewardHomeBenefits} aria-hidden="true">
                <span><i><ActionIcon type="shieldCheck" /></i>More transactions</span>
                <span><i><ActionIcon type="trophy" /></i>Bigger gifts</span>
                <span><i><ActionIcon type="users" /></i>Happier customers</span>
                <span><i><ActionIcon type="arrow" /></i>A stronger agent community</span>
              </div>
            </div>
          </Link>
        </div>

        <section className={styles.agentSection}>
          <div className={styles.sectionHeaderRow}>
            <h2 className={styles.sectionTitle}>PIN Details</h2>
            <Link to="/pin" className={styles.sectionLink}>
              View all
            </Link>
          </div>

          <div className={styles.pinDetailsCard}>
            {pinDetails.isLoading && (
              <div className={styles.pinStateMessage}>Loading PIN details...</div>
            )}

            {!pinDetails.isLoading && pinDetails.error && (
              <div className={styles.pinStateMessage}>{pinDetails.error}</div>
            )}

            {!pinDetails.isLoading && !pinDetails.error && (
                <div className={styles.pinStatsGridNew}>
                  <div className={styles.pinStatCard}>
                    <span>Total Active PINs</span>
                    <strong>{pinSummary.totalActive.toLocaleString()}</strong>
                  </div>
                  <div className={styles.pinStatCard}>
                    <span>Exportable PINs</span>
                    <strong>{pinSummary.exportable}</strong>
                  </div>
                  <div className={styles.pinStatCard}>
                    <span>Platform Types</span>
                    <strong>{pinSummary.platformBreakdown.length}</strong>
                  </div>
                </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default Home;
